import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type AutoReplyRule, type Device, type SourceSummary } from '../lib/api'
import { fmtNumber } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field, Loading, Modal, Pill } from '../components/ui'

export default function AutoReplies() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [editing, setEditing] = useState<AutoReplyRule | 'new' | null>(null)

  const rules = useQuery({
    queryKey: ['auto-replies'],
    queryFn: () => api.get<AutoReplyRule[]>('/api/admin/auto-replies'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/auto-replies/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['auto-replies'] }),
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Auto-replies</h1>
          <div className="page-sub">
            Rules are checked in priority order and the first match wins — one inbound message produces at most one
            reply. Scope a rule to a source so each application answers its own traffic in its own words.
          </div>
        </div>
        {can('Admin') && (
          <button className="btn primary" onClick={() => setEditing('new')}>
            New rule
          </button>
        )}
      </div>

      <Card flush>
        {rules.isLoading ? (
          <Loading />
        ) : rules.data?.length === 0 ? (
          <Empty
            title="No auto-reply rules"
            hint="Inbound messages are stored and forwarded, but nothing is answered automatically."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="num">Priority</th>
                  <th>Rule</th>
                  <th>Applies to</th>
                  <th>Matches</th>
                  <th>Mode</th>
                  <th className="num">Replied</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.data?.map((r) => (
                  <tr key={r.id}>
                    <td className="num faint">{r.priority}</td>
                    <td>
                      <strong>{r.name}</strong>
                      <div className="muted small" style={{ maxWidth: 320 }}>
                        {r.replyBody.length > 70 ? `${r.replyBody.slice(0, 70)}…` : r.replyBody}
                      </div>
                    </td>
                    <td>
                      <div className="row wrap" style={{ gap: 5 }}>
                        <span className="chip">{r.sourceName ?? 'any source'}</span>
                        {r.clientName && <span className="chip">{r.clientName}</span>}
                      </div>
                    </td>
                    <td>
                      <span className="mono small">
                        {r.matchType}
                        {r.matchValue ? `: ${r.matchValue}` : ''}
                      </span>
                    </td>
                    <td>
                      <Pill tone={r.mode === 'OncePerRecipient' ? 'info' : 'warn'}>
                        {r.mode === 'OncePerRecipient' ? 'once per number' : 'every message'}
                      </Pill>
                    </td>
                    <td className="num">{fmtNumber(r.repliedCount)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                        {!r.isActive && <span className="chip">off</span>}
                        {can('Admin') && (
                          <>
                            <button className="btn sm" onClick={() => setEditing(r)}>
                              Edit
                            </button>
                            <button className="btn danger sm" onClick={() => remove.mutate(r.id)}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['auto-replies'] })
          }}
        />
      )}
    </div>
  )
}

function RuleModal({
  rule,
  onClose,
  onDone,
}: {
  rule: AutoReplyRule | null
  onClose: () => void
  onDone: () => void
}) {
  const [name, setName] = useState(rule?.name ?? '')
  const [sourceId, setSourceId] = useState(rule?.sourceId ?? '')
  const [clientId, setClientId] = useState(rule?.clientId ?? '')
  const [matchType, setMatchType] = useState(rule?.matchType ?? 'keyword')
  const [matchValue, setMatchValue] = useState(rule?.matchValue ?? '')
  const [replyBody, setReplyBody] = useState(rule?.replyBody ?? '')
  const [mode, setMode] = useState(rule?.mode ?? 'OncePerRecipient')
  const [isActive, setIsActive] = useState(rule?.isActive ?? true)
  const [priority, setPriority] = useState(rule?.priority ?? 0)
  const [error, setError] = useState<string | null>(null)

  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.get<SourceSummary[]>('/api/admin/sources') })
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => api.get<Device[]>('/api/admin/devices') })

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        sourceId: sourceId || null,
        clientId: clientId || null,
        matchType,
        matchValue: matchType === 'any' ? null : matchValue,
        replyBody,
        mode,
        isActive,
        priority,
        quietHoursStart: rule?.quietHoursStart ?? null,
        quietHoursEnd: rule?.quietHoursEnd ?? null,
      }
      return rule ? api.put(`/api/admin/auto-replies/${rule.id}`, body) : api.post('/api/admin/auto-replies', body)
    },
    onSuccess: onDone,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the rule.'),
  })

  // Part count comes from the server's own encoder rather than a JavaScript approximation.
  // Extension-table characters (euro, braces, brackets, backslash, caret, tilde, pipe) cost two
  // septets each, so a local guess understates exactly the messages that cost the most — which
  // defeats the point of showing a count at all.
  const preview = replyBody.replace(/\{\{from\}\}/g, '+23057123456').replace(/\{\{clientName\}\}/g, 'Emtel-01')
  const encoding = useQuery({
    queryKey: ['preview-encoding', preview],
    queryFn: () =>
      api.post<{ encoding: string; partCount: number; characters: number }>(
        '/api/admin/preview-encoding',
        { text: preview },
      ),
    enabled: preview.length > 0,
    staleTime: 60_000,
  })
  const isUnicode = encoding.data?.encoding === 'ucs2'
  const parts = encoding.data?.partCount ?? 0

  return (
    <Modal
      title={rule ? `Edit ${rule.name}` : 'New auto-reply rule'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => save.mutate()}
            disabled={!name.trim() || !replyBody.trim() || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save rule'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <Field label="Rule name">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Opt-out confirmation" autoFocus />
      </Field>

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Source" hint="Only inbound routed to this source triggers the rule.">
          <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
            <option value="">Any source</option>
            {sources.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Device" hint="Optional. Restrict to messages arriving on one SIM.">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Any device</option>
            {devices.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {!sourceId && (
        <div className="banner warn">
          <div>
            With no source selected this rule answers inbound from <strong>every</strong> source. Scope it unless
            you intend a fleet-wide catch-all.
          </div>
        </div>
      )}

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Match type">
          <select value={matchType} onChange={(e) => setMatchType(e.target.value)}>
            <option value="any">Any message</option>
            <option value="keyword">Keyword (whole body)</option>
            <option value="prefix">Prefix (first word)</option>
            <option value="regex">Regular expression</option>
          </select>
        </Field>
        {matchType !== 'any' && (
          <Field label="Match value">
            <input
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}
              placeholder={matchType === 'regex' ? '^BAL(ANCE)?$' : 'STOP'}
              className="mono"
            />
          </Field>
        )}
      </div>

      <Field
        label="Reply body"
        hint="Placeholders: {{from}}, {{to}}, {{body}}, {{received}}, {{clientName}}, {{operator}}."
      >
        <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={3} />
      </Field>
      <div className="row between faint small" style={{ marginTop: -6, marginBottom: 14 }}>
        <span>
          {preview.length} characters · {isUnicode ? 'Unicode' : 'GSM-7'}
        </span>
        <span style={{ color: parts > 1 ? 'rgb(var(--warn))' : undefined }}>
          {parts} SMS part{parts === 1 ? '' : 's'}
        </span>
      </div>

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Mode">
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="OncePerRecipient">Once per number, forever</option>
            <option value="Always">Every matching message</option>
          </select>
        </Field>
        <Field label="Priority" hint="Higher is checked first. First match wins.">
          <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </Field>
      </div>

      {mode === 'Always' && (
        <div className="banner warn">
          <div>
            Replying to every message can loop against another auto-responder. Loop protection caps this at 3
            replies per number per hour and 100 fleet-wide per hour, but prefer once-per-number where it fits.
          </div>
        </div>
      )}

      <label className="check">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
    </Modal>
  )
}
